import { useContext } from "react"
import { useFragment } from "react-relay"
import { useLocation, useNavigate, useParams } from "react-router"
import { getChatRoute } from "@/modules/chats/lib/routing-helpers"
import { hasDeBetterDocRecommendedCareProviders } from "@/modules/patients/detail/better-doc/de/lib/has-de-better-doc-recommended-care-providers"
import { generateActiveAndInactiveMedicationList } from "@/modules/patients/detail/medication/hooks/useMedications"
import { PATIENT_ROUTE } from "@/modules/patients/detail/patient-navbar"
import {
  ProgressNoteForm,
  ProgressNoteFormContext,
  useProgressNoteFormContext,
} from "@/modules/patients/detail/progress-note/hooks/useProgressNoteFormStore"
import { buildChCarePlanData } from "@/modules/patients/detail/progress-note/lib/build-ch-care-plan-data"
import { getRecommendedMedications } from "@/modules/patients/detail/progress-note/lib/is-recommended-medications-empty"
import { scrollToFirstValidationError } from "@/modules/patients/detail/progress-note/lib/scroll-to-first-validation-error"
import { ProgressNoteData } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"
import { useConcludeDialogStoreContext } from "@/modules/patients/detail/store/useConcludeDialogStore"
import { useDocumentStore } from "@/modules/patients/detail/store/useDocumentStore"
import { useEpisodeOfCareStoreContext } from "@/modules/patients/detail/store/useEpisodeOfCareStore"
import { useAppointmentParam } from "@/modules/patients/hooks/useAppointmentParam"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import { PatientFragment$key } from "@/modules/patients/services/__generated__/PatientFragment.graphql"
import {
  CarePlanAttestationGuardianInput,
  CarePlanFollowUpInput,
  CarePlanGeneralAttestationInput,
  CarePlanPatientInput,
  CarePlanSickLeaveInput,
  CarePlanTreatmentPlanInput,
  Ch_saveCarePlanDataInput,
  chSaveCarePlanDataMutation$data,
} from "@/modules/patients/services/ch/__generated__/chSaveCarePlanDataMutation.graphql"
import {
  De_saveCarePlanDataInput,
  deSaveCarePlanDataMutation$data,
} from "@/modules/patients/services/de/__generated__/deSaveCarePlanDataMutation.graphql"
import { PatientFragment } from "@/modules/patients/services/PatientFragment"
import { usePatientByIdQuery } from "@/modules/patients/services/usePatientByIdQuery"
import { useSaveCarePlanDataMutation } from "@/modules/patients/services/useSaveCarePlanDataMutation"
import { CommunicationChannel } from "@/types/__generated__/union-types"

type SaveCarePlanDataInput = Ch_saveCarePlanDataInput | De_saveCarePlanDataInput

type ConcludeProps = {
  onValid?: () => void
}

// Resolves the chat thread route to navigate to after concluding.
type GetChatThreadRoute = (threadId: string) => string

const useHandleConcludeInternal = (getChatThreadRoute: GetChatThreadRoute) => {
  const { search, pathname } = useLocation()
  const country = useCountryParam()
  const navigate = useNavigate()
  const { threadId } = useParams()
  const patientId = usePatientParam()
  const appointmentId = useAppointmentParam()
  const saveCarePlanData = useSaveCarePlanDataMutation()
  const updateProgressNoteCarePlanSummaryDocument = useDocumentStore(
    (state) => state.actions.updateProgressNoteCarePlanSummaryDocument,
  )

  const [patientData] = usePatientByIdQuery(patientId, country, appointmentId)
  const patientFragmentData = useFragment<PatientFragment$key>(PatientFragment, patientData.patientById)

  const progressNoteForms = useProgressNoteFormContext((state) => state.progressNoteForms)
  const progressNoteFormStore = useContext(ProgressNoteFormContext)
  if (!progressNoteFormStore) throw new Error("Missing ProgressNoteFormProvider in the tree")

  const setMarkdownPreviews = useConcludeDialogStoreContext((state) => state.actions.setMarkdownPreviews)
  const setEpisodeOfCareFragmentReference = useEpisodeOfCareStoreContext(
    (state) => state.setEpisodeOfCareFragmentReference,
  )

  if (!patientData || !patientFragmentData) {
    return
  }

  const { activeMedications, inactiveMedications } = generateActiveAndInactiveMedicationList(
    patientData.patientById?.medications?.edges ?? [],
    country,
  )

  const isRouteMatched = (route: string) => pathname.includes(route)

  const navigateAfterConclude = () => {
    if (threadId) {
      navigate(getChatThreadRoute(threadId))
    } else if (isRouteMatched(PATIENT_ROUTE.INFORMATION) || isRouteMatched(PATIENT_ROUTE.AGENDA)) {
      navigate(`/patients/${country}/${patientId}${search}`, { state: { fromConclude: true } })
    }
  }

  const validateQuestionSummaryForm = async (item: ProgressNoteForm) => {
    let isQuestionSummaryFormValid: boolean | undefined
    if (item.episodeOfCareProgressNoteData.appointment.channel === "ASYNC_REQUEST") {
      isQuestionSummaryFormValid = await item?.questionnaireForm?.trigger()
      const button = document.querySelector<HTMLButtonElement>("button.questionSummaryButton")

      if (button && !isQuestionSummaryFormValid) {
        button.click()
      }
    }

    return isQuestionSummaryFormValid
  }

  const validateBetterDocMSNReferralLetter = async (item: ProgressNoteForm) => {
    if (
      item.pointOfCareForm?.getValues().betterDocSuggestionStatus === "MANUAL_SEARCH_NEEDED" &&
      !item.episodeOfCareProgressNoteData.referralLetter
    ) {
      progressNoteFormStore
        .getState()
        .setProgressNoteForm(item.episodeOfCareProgressNoteData.id, item.episodeOfCareProgressNoteData, {
          kind: "BetterDocMSNReferralLetter",
          data: {
            hasErrors: true,
          },
        })

      return false
    }

    progressNoteFormStore
      .getState()
      .setProgressNoteForm(item.episodeOfCareProgressNoteData.id, item.episodeOfCareProgressNoteData, {
        kind: "BetterDocMSNReferralLetter",
        data: {
          hasErrors: false,
        },
      })

    return true
  }

  const validateDeBetterDocReferralLetter = async (item: ProgressNoteForm) => {
    const { episodeOfCareProgressNoteData } = item
    const isReferralLetterMissing =
      hasDeBetterDocRecommendedCareProviders(episodeOfCareProgressNoteData) &&
      !episodeOfCareProgressNoteData.referralLetter

    progressNoteFormStore
      .getState()
      .setProgressNoteForm(episodeOfCareProgressNoteData.id, episodeOfCareProgressNoteData, {
        kind: "DeBetterDocReferralLetter",
        data: {
          hasErrors: isReferralLetterMissing,
        },
      })

    return !isReferralLetterMissing
  }

  const validate = async (item: ProgressNoteForm) => {
    const isAnamnesisFormValid = await item.anamnesisForm?.trigger()
    const isAssignedEpisodeOfCareFormValid = await item.assignEpisodeOfCareForm?.trigger()
    const isTreatmentPlanFormValid = item.treatmentPlanForm?.getValues().isTreatmentPlanOptional
      ? item.treatmentPlanForm?.getValues().isTreatmentPlanOptional
      : await item.treatmentPlanForm?.trigger()
    const isPointOfCareFormValid = await item?.pointOfCareForm?.trigger()
    const isQuestionSummaryFormValid = await validateQuestionSummaryForm(item)
    const isBetterDocMSNReferralLetterValid = await validateBetterDocMSNReferralLetter(item)
    const isDeBetterDocReferralLetterValid = await validateDeBetterDocReferralLetter(item)

    return {
      isAnamnesisFormValid,
      isAssignedEpisodeOfCareFormValid,
      isTreatmentPlanFormValid,
      isPointOfCareFormValid,
      isQuestionSummaryFormValid,
      isBetterDocMSNReferralLetterValid,
      isDeBetterDocReferralLetterValid,
    }
  }

  const validateAdministrative = async (item: ProgressNoteForm) => {
    const isAnamnesisFormValid = await item.anamnesisForm?.trigger()
    const isQuestionSummaryFormValid = await validateQuestionSummaryForm(item)

    return {
      isAnamnesisFormValid,
      isQuestionSummaryFormValid,
    }
  }

  const commitSaveCarePlanDataMutation = async (progressNote: ProgressNoteData) => {
    if (!progressNote.treatmentPlan) return

    const patient: CarePlanPatientInput = {
      firstName: patientFragmentData.firstName,
      lastName: patientFragmentData.lastName,
      birthDate: patientFragmentData.birthDate,
    }

    const treatmentPlan: CarePlanTreatmentPlanInput = {
      title: progressNote.treatmentPlan.title,
      from: progressNote.treatmentPlan.from,
      to: progressNote.treatmentPlan.to,
      recommendedTreatment: progressNote.treatmentPlan.recommended ?? "",
      followUpTreatment: progressNote.treatmentPlan.followUpInstructions ?? "",
    }

    const selectedAttestationGuardian = progressNote.attestationGuardian
    const attestationGuardian: CarePlanAttestationGuardianInput | null = selectedAttestationGuardian
      ? {
          from: selectedAttestationGuardian.durationFrom,
          to: selectedAttestationGuardian.durationTo,
        }
      : null

    const followUp: CarePlanFollowUpInput | null = progressNote.followUpAppointment
      ? {
          slotId: progressNote.followUpAppointment.slotId,
          date: progressNote.followUpAppointment.date,
        }
      : null

    const sickLeave: CarePlanSickLeaveInput | null = progressNote.sickLeave
      ? {
          from: progressNote.sickLeave?.from,
          to: progressNote.sickLeave?.to,
        }
      : null

    const generalAttestation: CarePlanGeneralAttestationInput | null = progressNote.generalAttestation
      ? {
          name: progressNote.generalAttestation.name,
          subject: progressNote.generalAttestation.subject,
        }
      : null

    const saveCarePlanDataInput: SaveCarePlanDataInput = {
      progressNoteId: progressNote.id,
      keepOriginalAuthor: false,
      patientId,
      patient,
      appointmentId: progressNote.appointment.id,
      appointmentLanguage: progressNote.appointment.language,
      treatmentPlan,
      medications: [],
      recommendedMedications: getRecommendedMedications(progressNote).map(({ node }) => {
        const { name, morningDosage, noonDosage, eveningDosage, nightDosage, usageInstructions, unit } = node.medication
        return {
          eveningDosage,
          morningDosage,
          name,
          nightDosage,
          noonDosage,
          usageInstruction: usageInstructions,
          asNeeded: unit === "PRN",
        }
      }),
      followUp,
      sickLeave,
      attestationGuardian,
      generalAttestation,
      therapies: [],
    }

    if (progressNote.__typename === "ch_ProgressNote") {
      saveCarePlanDataInput.careProviderId = progressNote.pointOfCare?.careProvider?.id

      const { medications, therapies } = buildChCarePlanData(progressNote, [
        ...activeMedications,
        ...inactiveMedications,
      ])

      saveCarePlanDataInput.medications = medications
      saveCarePlanDataInput.therapies = therapies
    }

    const response = await saveCarePlanData(saveCarePlanDataInput)

    if (response.saveCarePlanData.carePlanPreviewOutput?.carePlanSummary) {
      updateProgressNoteCarePlanSummaryDocument(
        country,
        progressNote.id,
        response.saveCarePlanData.carePlanPreviewOutput.carePlanSummary,
      )
    }

    return response
  }

  const handleAllFormsValidation = async ({ onValid }: ConcludeProps) => {
    const progressNoteFormStoreSorted = new Map(
      [...progressNoteForms.entries()].sort((a, b) => {
        const progressNoteFragmentReferenceA = a[1].episodeOfCareProgressNoteData
        const progressNoteFragmentReferenceB = b[1].episodeOfCareProgressNoteData
        return (
          new Date(progressNoteFragmentReferenceB.updatedAt).getTime() -
          new Date(progressNoteFragmentReferenceA.updatedAt).getTime()
        )
      }),
    )

    let shouldOpenPreviewDialog = true
    let firstInvalidProgressNoteId = ""

    const progressNoteToGenerateCarePlanSummary: ProgressNoteData[] = []

    for (const [key, item] of progressNoteFormStoreSorted) {
      // We will have all progress notes form data from multiple appointment from the chat page
      // We will need to filter them by appointmentId
      if (item.episodeOfCareProgressNoteData.appointment.id !== appointmentId) {
        continue
      }

      let isProgressNoteValid = true
      const progressNote = item.episodeOfCareProgressNoteData
      const { kind } = progressNote

      switch (kind) {
        case "MEDICAL": {
          const {
            isAssignedEpisodeOfCareFormValid,
            isAnamnesisFormValid,
            isQuestionSummaryFormValid,
            isPointOfCareFormValid,
            isTreatmentPlanFormValid,
            isBetterDocMSNReferralLetterValid,
            isDeBetterDocReferralLetterValid,
          } = await validate(item)

          isProgressNoteValid = Boolean(
            isAssignedEpisodeOfCareFormValid &&
            isAnamnesisFormValid &&
            isCheckQuestionSummaryAndChannel(isQuestionSummaryFormValid, progressNote.appointment.channel) &&
            isPointOfCareFormValid &&
            isTreatmentPlanFormValid &&
            isBetterDocMSNReferralLetterValid &&
            isDeBetterDocReferralLetterValid,
          )
          break
        }
        case "ADMINISTRATIVE": {
          const { isAnamnesisFormValid, isQuestionSummaryFormValid } = await validateAdministrative(item)
          isProgressNoteValid = Boolean(
            isAnamnesisFormValid &&
            isCheckQuestionSummaryAndChannel(isQuestionSummaryFormValid, progressNote.appointment.channel),
          )
          break
        }
        default:
          break
      }

      if (!isProgressNoteValid && shouldOpenPreviewDialog) {
        shouldOpenPreviewDialog = false
        firstInvalidProgressNoteId = key
      } else {
        progressNoteToGenerateCarePlanSummary.push(progressNote)
      }
    }

    if (shouldOpenPreviewDialog) {
      let saveCarePlanDataResult: (chSaveCarePlanDataMutation$data | deSaveCarePlanDataMutation$data)[] = []

      for (const element of progressNoteToGenerateCarePlanSummary) {
        const response = await commitSaveCarePlanDataMutation(element)
        if (response) {
          saveCarePlanDataResult = [response, ...saveCarePlanDataResult]
        }
      }
      if (saveCarePlanDataResult.length > 0) {
        setMarkdownPreviews(saveCarePlanDataResult)
      }
      onValid?.()
    } else {
      scrollToFirstValidationError(firstInvalidProgressNoteId)
    }
  }

  const handleConclude = async ({ onValid }: ConcludeProps) => {
    navigateAfterConclude()
    setEpisodeOfCareFragmentReference(null)
    await handleAllFormsValidation({ onValid })
  }

  const isCheckQuestionSummaryAndChannel = (formValid: boolean | undefined, channel: CommunicationChannel): boolean => {
    if (formValid !== undefined) {
      return formValid && channel === "ASYNC_REQUEST"
    }
    return true
  }

  return handleConclude
}

const useHandleConclude = () => useHandleConcludeInternal(getChatRoute)

export { useHandleConclude }
